import { TextInput, Dropdown, PhoneInput, EmailInput, useTranslator, getLocale } from "@sia/style-guide";
import { useCallback, useContext, useEffect, useState } from "react";
import { WizarContext } from "contexts/WizardContext";
import { corporateMainAddressActionCreator } from "contexts/WizardContext";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapter";
import pgAdapter from "common/Adapters/PGAdapter/PGAdapter";
import GlobalContext from "contexts/GlobalContext";
import { initialWizardFormState } from "contexts/WizardContext";

const MainAddress = ({ errors, setErrors }) => {
  const translate = useTranslator();
  const { language } = useContext(GlobalContext);
  const { state, dispatch } = useContext(WizarContext);
  let corporateMainAddress = state.corporate.mainAddress;
  let corporateMainAddressErrors = errors.corporate.mainAddress;
  let changState = useCallback((payload) => dispatch(corporateMainAddressActionCreator(payload)), [dispatch]);

  const resetInputFieldError = (property, error) => {
    const exculdedProperties = ["phone", "mobile", "email"];
    if (!exculdedProperties.includes(property)) {
      setErrors((errorsState) => ({
        ...errorsState,
        corporate: {
          ...errorsState.corporate,
          mainAddress: { ...errorsState.corporate.mainAddress, [property]: error },
        },
      }));
    } else {
      exculdedProperties.forEach((exculdedProperty) => {
        if (property === exculdedProperty)
          setErrors((errorsState) => ({
            ...errorsState,
            corporate: {
              ...errorsState.corporate,
              mainAddress: { ...errorsState.corporate.mainAddress, [exculdedProperty]: error },
            },
          }));
      });
    }
  };

  const [countries, setCountries] = useState([]);
  const [cantons, setCantons] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const data_countries = await webPlatformAdapter.request("GET", "rpc/get_countries_list", { lang: getLocale(language) });
      let countries = data_countries.map((e) => ({ ...e, label: e.name }));
      setCountries(countries);
      const data_states = await webPlatformAdapter.request("GET", "rpc/get_states_list", { lang: getLocale(language) });
      let cantons = data_states.map((e) => ({ ...e, label: e.name }));
      setCantons(cantons);
    };
    fetchData();
  }, [language]);

  const { user } = useContext(GlobalContext);
  const userId = user.siaId;

  useEffect(() => {
    if (userId) {
      pgAdapter.getWizardSession(({ data }) => {
        const defaultMainAddress = { ...initialWizardFormState.corporate.mainAddress };
        const { mainAddress } = data?.corporate || {};
        const { street, streetTwo, zip, city, country, canton, phone, email } = mainAddress || defaultMainAddress;
        changState({ street, streetTwo, zip, city, country, canton, phone, email });
      });
    }
  }, [userId, changState]);

  const setUserSession = (obj) => {
    pgAdapter.getWizardSession(({ data }) => {
      const updatedSession = {
        ...data,
        corporate: { ...(data.corporate || {}), mainAddress: { ...(data?.corporate?.mainAddress || {}), ...obj } },
      };
      pgAdapter.setWizardSession({ ...updatedSession });
    });
  };

  return (
    <div className="corporate-info-page">
      <div className="sia-margin-03-top"></div>
      <TextInput
        onBlur={() => {
          setUserSession({ street: corporateMainAddress.street });
        }}
        id={"corporate-page-corporate-street"}
        value={corporateMainAddress.street}
        onChange={(event, value) => {
          resetInputFieldError("street");
          changState({ street: value });
        }}
        label={translate("wizard.form.corporate.mainAddress.street.label")}
        placeholder={translate("wizard.form.single.personal-info.steet.placeholder")}
        required={true}
        invalidPatternText={corporateMainAddressErrors.street}
        invalidText={corporateMainAddressErrors.street}
      />
      <TextInput
        onBlur={() => {
          setUserSession({ streetTwo: corporateMainAddress.streetTwo });
        }}
        id={"corporate-page-corporate-street2"}
        value={corporateMainAddress.streetTwo}
        onChange={(event, value) => {
          resetInputFieldError("streetTwo");
          changState({ streetTwo: value });
        }}
        label={translate("wizard.form.corporate.mainAddress.street2.label")}
        placeholder={translate("wizard.form.single.personal-info.steet.placeholder")}
        invalidPatternText={corporateMainAddressErrors.streetTwo}
        invalidText={corporateMainAddressErrors.streetTwo}
      />
      <TextInput
        onBlur={() => {
          setUserSession({ zip: corporateMainAddress.zip });
        }}
        id={"corporate-page-corporate-zip"}
        value={corporateMainAddress.zip}
        placeholder={translate("wizard.form.single.personal-info.zip.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("zip");
          changState({ zip: value });
        }}
        label={translate("wizard.form.single.personal-info.zip.label")}
        required={true}
        invalidPatternText={corporateMainAddressErrors.zip}
        invalidText={corporateMainAddressErrors.zip}
      />
      <TextInput
        onBlur={() => {
          setUserSession({ city: corporateMainAddress.city });
        }}
        id={"corporate-page-corporate-city"}
        value={corporateMainAddress.city}
        placeholder={translate("wizard.form.single.personal-info.city.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("city");
          changState({ city: value });
        }}
        label={translate("wizard.form.single.personal-info.city.label")}
        required={true}
        invalidPatternText={corporateMainAddressErrors.city}
        invalidText={corporateMainAddressErrors.city}
      />

      <Dropdown
        id={"corporate-page-country"}
        label={translate("wizard.form.single.personal-info.country.label")}
        items={countries}
        value={corporateMainAddress.country}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("country");
          changState({ country: value });
          setUserSession({ country: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalidPatternText={corporateMainAddressErrors.country}
        invalidText={corporateMainAddressErrors.country}
      />
      <Dropdown
        id={"corporate-page-canton"}
        label={translate("wizard.form.single.personal-info.canton.label")}
        items={cantons}
        value={corporateMainAddress.canton}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("canton");
          changState({ canton: value });
          setUserSession({ canton: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalidPatternText={corporateMainAddressErrors.canton}
        invalidText={corporateMainAddressErrors.canton}
      />
      <PhoneInput
        onBlur={() => {
          setUserSession({ phone: corporateMainAddress.phone });
        }}
        id={"corporate-page-phone"}
        label={translate("wizard.form.corporate.mainAddress.phone.label")}
        placeholder={translate("idp.register.corporate.phone.placeholder")}
        value={corporateMainAddress.phone}
        onChange={(event, value, error) => {
          resetInputFieldError("phone", error);
          changState({ phone: value });
        }}
        invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.phone") })}
        invalidText={corporateMainAddressErrors.phone}
      />

      <EmailInput
        onBlur={() => {
          setUserSession({ email: corporateMainAddress.email });
        }}
        id={"corporate-page-email"}
        label={translate("wizard.form.single.personal-info.email.label")}
        placeholder={translate("idp.register.corporate.email.placeholder")}
        value={corporateMainAddress.email}
        onChange={(event, value, error) => {
          resetInputFieldError("email", error);
          changState({ email: value });
        }}
        invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.email") })}
        invalidText={corporateMainAddressErrors.email}
      />
    </div>
  );
};

export default MainAddress;
